import React, { useMemo } from "react";
import { cn } from "../utils";
import { Skeleton } from "./Skeleton";

const rand = (min, max) => Math.round(min + Math.random() * (max - min));

/**
 * One section card of a detail page: a 120px header line, then a grid of
 * label/value pairs (60px label, 70–90% value).
 */
export function DetailSectionSkeleton({ fields = 8, columns = 4, label = "Loading details", className }) {
  const widths = useMemo(() => Array.from({ length: fields }, () => rand(70, 90)), [fields]);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn("bg-white rounded-lg border border-solid border-gray-200 p-4", className)}
    >
      <Skeleton variant="line" width={120} className="mb-5" />
      <div
        className="grid gap-x-6 gap-y-5"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {widths.map((w, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton variant="line" width={60} />
            <Skeleton variant="line" width={`${w}%`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Dashboard card / KPI placeholder. Keeps the card box; pass `chartHeight`
 * for cards that hold a chart.
 */
export function KpiCardSkeleton({ chartHeight, label = "Loading", className }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn("bg-white rounded-lg border border-solid border-gray-200 p-4 flex flex-col gap-3", className)}
    >
      <Skeleton variant="line" width="40%" />
      <Skeleton width="60%" style={{ height: 20 }} />
      {chartHeight ? <Skeleton width="100%" style={{ height: chartHeight }} /> : null}
    </div>
  );
}

/** Three skeleton rows plus "Loading…", for menus that fetch their options. */
export function MenuSkeleton({ className }) {
  const widths = useMemo(() => [rand(55, 80), rand(45, 70), rand(50, 75)], []);
  return (
    <div role="status" aria-live="polite" aria-label="Loading options" className={cn("px-3 py-2", className)}>
      {widths.map((w, i) => (
        <div key={i} className="py-2">
          <Skeleton variant="line" width={`${w}%`} />
        </div>
      ))}
      <div className="pt-1 text-xs text-gray-500">Loading…</div>
    </div>
  );
}

/** Drop-in for react-select: `components={{ LoadingMessage: SelectLoadingMessage }}`. */
export function SelectLoadingMessage() {
  return <MenuSkeleton className="text-left" />;
}
